// src/plugins/block-paste-drop-images.plugin.js

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';

export default class BlockPasteDropImagesPlugin extends Plugin {
	static get pluginName() {
		return 'BlockPasteDropImagesPlugin';
	}

	init() {
		const editor = this.editor;

		const cfg        = editor.config.get('blockPasteDropImages') || {};
		const enabled    = cfg.enabled !== false;     // default: true
		const blockPaste = cfg.blockPaste !== false;  // default: true
		const blockDrop  = cfg.blockDrop !== false;   // default: true
		const callback   = typeof cfg.callback === 'function' ? cfg.callback : null;

		if (!enabled) {
			return;
		}

		const viewDocument = editor.editing.view.document;

		// ---------------- PASTE ----------------
		if (blockPaste) {
			this.listenTo(
				viewDocument,
				'clipboardInput',
				(evt, data) => {
					const dt = data.dataTransfer;
					if (!dt) return;

					const files = Array.from(dt.files || []);
					const html  = dt.getData('text/html') || '';

					const hasImageFile = files.some(f => f && f.type && f.type.startsWith('image/'));
					const hasImageHtml = /<img\s/i.test(html);

					// 👇 IMPORTANT: data.content is already a ViewFragment at this point
					// We'll strip <img> from it, not from raw HTML
					if (hasImageHtml && data.content) {
						// walk through the view fragment and remove image elements
						const view = editor.editing.view;
						const writer = view.change( writer => {
							// we can't mutate here directly, so return writer
							return writer;
						});
						// we can't actually use the writer like that, so simpler: manual walker

						// manual walk over data.content
						const walker = data.content.getWalker();
						const toRemove = [];

						for (const item of walker) {
							const node = item.item;
							// CKEditor inline images often come as <img> or <figure class="image">
							if (node.is && node.is('element', 'img')) {
								toRemove.push(node);
							}
							if (node.is && node.is('element', 'figure') && node.getClassNames().has('image')) {
								toRemove.push(node);
							}
						}

						// now actually remove them
						this.editor.editing.view.change( writer => {
							for (const node of toRemove) {
								writer.remove(node);
							}
						});

						// still notify app
						const payload = {
							source: 'paste',
							files: [],        // no real files here, just inline <img>
							dataTransfer: dt,
							html
						};

						if (callback) {
							callback(payload, editor);
						} else {
							editor.fire('pastedOrDroppedImages', payload);
						}

						// ⚠️ DO NOT stop the event — we want mention/text to go through
						return;
					}

					// real image files from clipboard → block insert
					if (hasImageFile) {
						evt.stop();
						if (typeof data.preventDefault === 'function') {
							data.preventDefault();
						}

						const payload = {
							source: 'paste',
							files: files,
							dataTransfer: dt,
							html
						};

						if (callback) {
							callback(payload, editor);
						} else {
							editor.fire('pastedOrDroppedImages', payload);
						}
					}
				},
				{ priority: 'highest' }
			);
		}

		// ---------------- DROP ----------------
		if (blockDrop) {
			this.listenTo(
				viewDocument,
				'drop',
				(evt, data) => {
					const dt = data.dataTransfer;
					if (!dt) return;

					const files = Array.from(dt.files || []);
					const hasImageFile = files.some(f => f && f.type && f.type.startsWith('image/'));

					if (hasImageFile) {
						evt.stop();
						if (typeof data.preventDefault === 'function') {
							data.preventDefault();
						}

						const payload = {
							source: 'drop',
							files: files,
							dataTransfer: dt
						};

						if (callback) {
							callback(payload, editor);
						} else {
							editor.fire('pastedOrDroppedImages', payload);
						}
					}
				},
				{ priority: 'highest' }
			);
		}
	}
}
